// src/context/ConsentContext.js
import React, { createContext, useState, useEffect } from 'react';
import api from '../utils/api';

export const ConsentContext = createContext();

// Single-flight consent fetch (dedupe network calls) - module scope
let inFlightConsentPromise = null;

async function fetchConsentCurrent() {
  if (inFlightConsentPromise) return inFlightConsentPromise;
  
  inFlightConsentPromise = api.get('/api/consent/current/', { timeout: 1500 })
    .then(res => res.data)
    .catch(err => {
      console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
      throw err;
    })
    .finally(() => { 
      inFlightConsentPromise = null; 
    });
  
  return inFlightConsentPromise;
}

/**
 * Manages consent for authenticated users and guests:
 * - Uses GET /api/consent/current/ (works for both authenticated and guests)
 * - submitConsent posts to the correct endpoint based on authentication
 */
export const ConsentProvider = ({ children }) => {
  // sessionId MUST be created synchronously (not in useEffect)
  const [sessionId] = useState(() => {
    try {
      let sid = localStorage.getItem('sessionId');
      if (!sid) {
        sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
        localStorage.setItem('sessionId', sid);
      }
      return sid;
    } catch (e) {
      // Fallback if localStorage fails
      return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    }
  });

  // Cache-first: read from localStorage immediately
  const [consent, setConsent] = useState(() => {
    try {
      const cached = localStorage.getItem('consent_cached');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.has_consent === true && parsed?.consent_required !== true) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return null;
  });
  
  const [loading, setLoading] = useState(false); // Start with false - don't block rendering

  // Helper: check if user is authenticated
  const isAuthenticated = () => {
    try {
      return Boolean(localStorage.getItem('authToken'));
    } catch {
      return false;
    }
  };

  // Background verification: reconcile cache with backend
  useEffect(() => {
    const init = async () => {
      try {
        const data = await fetchConsentCurrent();
        
        // Handle session_id if provided by backend
        if (data.session_id && data.session_id !== sessionId) {
          localStorage.setItem('sessionId', data.session_id);
        }
        
        // Reconcile: backend is authoritative
        if (data.has_consent === true && data.consent_required !== true) {
          // Backend confirms consent - hydrate cache
          const consentData = { ...data, has_consent: true, consent_required: false };
          localStorage.setItem('consent_cached', JSON.stringify(consentData));
          localStorage.setItem('cookie_consent_given', 'true');
          setConsent(consentData);
        } else if (data.has_consent === false || data.consent_required === true) {
          // Backend definitively says no consent or consent required
          localStorage.removeItem('consent_cached');
          localStorage.removeItem('cookie_consent_given');
          setConsent(null);
        }
      } catch (err) {
        // Network error or timeout - keep existing cache, don't clear it
        // This allows cache-first behavior to continue working
        console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
      }
    };
    
    // Verify in background (non-blocking)
    init();
  }, [sessionId]);

  const submitConsent = async ({ consents, timestamp, appVersion, version }) => {
    const payload = { consents, timestamp, app_version: appVersion, privacy_policy_version: version };
    try {
      let res;
      let saved;
      
      // Use authentication status, NOT sessionId existence
      if (isAuthenticated()) {
        // User consent
        res = await api.post('/api/user/consent/', payload);
      } else {
        // Guest consent
        res = await api.post('/api/guest/consent/', { ...payload, session_id: sessionId });
      }
      
      // Backend sets cookie and returns has_consent/consent_required
      const result = res.data || {};
      saved = { 
        ...result, 
        ...payload,
        session_id: isAuthenticated() ? undefined : sessionId,
        has_consent: result.has_consent !== false, // Default to true if not explicitly false
        consent_required: false, // After submission, consent is no longer required
      };
      
      // Persist to cache immediately
      localStorage.setItem('consent_cached', JSON.stringify(saved));
      localStorage.setItem('cookie_consent_given', 'true');
      setConsent(saved);
      
      return saved;
    } catch (err) {
      console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
      throw err; // Re-throw so caller can handle
    }
  };

  const updateConsent = async ({ consents, timestamp, appVersion }) => {
    if (!isAuthenticated()) throw new Error('Guests must resubmit consent.');
    const payload = { consents, timestamp, app_version: appVersion };
    await api.put('/api/user/consent/update/', payload);
    setConsent({ ...consent, ...payload });
  };

  const withdrawAll = async (reason) => {
    if (isAuthenticated()) {
      await api.delete('/api/user/consent/withdraw/', { data: { reason } });
    } else {
      await api.delete('/api/guest/consent/withdraw/', { data: { session_id: sessionId, reason } });
    }
    setConsent(null);
    localStorage.removeItem('consent_cached');
    localStorage.removeItem('cookie_consent_given');
  };

  /**
   * Optional best-effort consent acceptance (called after submitConsent)
   * Ensures CSRF cookie exists and calls accept endpoint
   */
  const acceptConsent = async () => {
    try {
      // Ensure CSRF cookie exists
      if (!document.cookie.includes('csrftoken=')) {
        try {
          await api.get('/api/set-csrf/');
        } catch (e) {
          // Ignore CSRF setup errors
        }
      }
      
      const res = await api.post('/api/consent/accept/');
      const result = res.data || {};
      
      // Update consent state
      const updatedConsent = {
        ...consent,
        has_consent: true,
        consent_required: false,
        accepted_at: result.accepted_at,
        version: result.version,
      };
      setConsent(updatedConsent);
      localStorage.setItem('consent_cached', JSON.stringify(updatedConsent));
      localStorage.setItem('cookie_consent_given', 'true');
      
      return result;
    } catch (err) {
      console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
      // Don't throw - this is best-effort
      return null;
    }
  };

  return (
    <ConsentContext.Provider value={{ consent, loading, submitConsent, updateConsent, withdrawAll, acceptConsent, sessionId }}>
      {children}
    </ConsentContext.Provider>
  );
};
